import {
  createLocalAudioTrack,
  LocalAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  RoomConnectOptions,
  LogLevel,
  setLogLevel,
} from "livekit-client";
import { CallRoomModel } from "./CallRoomModel";
import { CallSessionModel, CallSessionStatus } from "./CallSessionModel";
import { InboundCallServiceClient } from "@/serviceClients/InboundCallServiceClient";
import { isKokattoErrorResponse } from "@/serviceClients/responses/KokattoErrorResponse";
import { CallConnectionModel } from "./CallConnectionModel";
import store from "@/store";
import {
  InboundCallAcceptRequest,
  InboundCallRejectIncomingRequest,
} from "@/serviceClients/requests/InboundCallRejectIncomingRequest";
import { ProcessingIncomingCallResponse } from "@/serviceClients/responses/KokattoCallCenterResponse";
import { RefreshableRequestHelper } from "@/helpers/RefreshableRequestHelper";
import {
  GetCallRoomTokenResponse,
  HoldCallRoomResponse,
  JoinCallRoomResponse,
  LeaveCallRoomResponse,
} from "@/serviceClients/responses/InboundCallResponse";
import { ERROR_REASON } from "@/store/modules/ActiveCallController/constants";
import { HoldCallRoomRequest } from "@/serviceClients/requests/InboundCallRequest";
/**
 * Represents the possible states of a call's hold status
 * @enum {string}
 */
export enum HoldStatus {
  LOADING = "loading",
  ON = "on",
  OFF = "off",
}

/**
 * Interface defining the core functionality for managing a call room
 * @interface CallRoomController
 */
export interface CallRoomController {
  /** The call room model containing room details */
  room: CallRoomModel;
  /** The call session model containing session information */
  session: CallSessionModel;
  /** Indicates if the audio is muted */
  onMute: boolean;
  /** Current hold status of the call */
  holdStatus: HoldStatus;
  /** Indicates if call transfer is in progress */
  isTransferring: boolean;
  /** Indicates if the call UI is expanded */
  isExpanded: boolean;

  /** Connects to the call room */
  connectToRoom: () => Promise<CallRoomModel | Error>;
  /** Toggles audio mute state */
  toggleAudioMute: () => void;
  /** Toggles call hold state */
  toggleHoldState: () => Promise<CallRoomModel | Error>;
  /** Disconnects from the call room */
  disconnectFromRoom: () => Promise<CallRoomModel | { err: string }>;
  /** Toggles call UI expansion state */
  toggleUIExpansion: () => void;
}

/**
 * Implementation of CallRoomController using LiveKit for real-time communication
 * @implements {CallRoomController}
 */
export class CallRoomLivekitController implements CallRoomController {
  room: CallRoomModel;
  session: CallSessionModel;
  onMute: boolean;
  holdStatus: HoldStatus;
  isTransferring: boolean;
  private audioElement: LocalAudioTrack | null = null;
  livekitRoom: Room | null;
  isExpanded: boolean;
  inboundCallServiceClient: InboundCallServiceClient;

  /**
   * Creates a new LiveKit-based call room controller
   * @param {CallRoomModel} _room - The call room model
   * @param {CallSessionModel} _session - The call session model
   */
  constructor(_room: CallRoomModel, _session: CallSessionModel) {
    this.room = _room;
    this.session = _session;
    this.livekitRoom = new Room();
    this.onMute = false;
    this.holdStatus = HoldStatus.OFF;
    this.isTransferring = false;
    this.isExpanded = true;
    this.inboundCallServiceClient = new InboundCallServiceClient({
      token: store.getters.getKokattoTokenAccess,
    });
  }

  /**
   * Establishes connection to the LiveKit room and initializes audio
   * @returns {Promise<CallRoomModel | Error>} The connected room model or error
   * @throws {Error} If connection fails or participant details are missing
   */
  async connectToRoom(): Promise<CallRoomModel | Error> {
    try {
      const clientId = store.getters.getClientId;
      const csDetails = store.getters.getCustomerService;
      if (!(clientId && csDetails)) {
        throw new Error(
          "Missing participant details: Client ID or CS details not found"
        );
      }

      const micStatus = await this._checkMicPermission();
      if (micStatus !== "granted") {
        if (micStatus === "denied") {
          throw new Error(ERROR_REASON.MIC_DENIED);
        } else {
          throw new Error(ERROR_REASON.MIC_NOT_GRANTED);
        }
      }

      if (this.livekitRoom) {
        const participantId = `CS-${clientId}-${csDetails.email}`;
        const tokenRequest = {
          roomId: this.room.roomId,
          sessionId: this.session.id,
          roomName: this.room.roomName || "",
          participantIdentity: participantId,
        };

        // Get room token
        const tokenResponse =
          await RefreshableRequestHelper.requestKokatto<GetCallRoomTokenResponse>(
            () => this.inboundCallServiceClient.getCallRoomToken(tokenRequest)
          );

        if (isKokattoErrorResponse(tokenResponse)) {
          const errorMessage = tokenResponse.message || tokenResponse.reason;
          if (errorMessage?.includes("Session is not ringing")) {
            throw new Error(
              `Failed to obtain room token: ${ERROR_REASON.SESSION_NOT_RINGING}`
            );
          }
          throw new Error(`Failed to obtain room token: ${errorMessage}`);
        }

        // Join room
        const joinRequest = {
          roomId: this.room.roomId,
          sessionId: this.session.id,
          remoteId: this.session.remoteId,
          csId: csDetails.id,
          csName: csDetails.csName,
        };

        const joinResponse =
          await RefreshableRequestHelper.requestKokatto<JoinCallRoomResponse>(
            () => this.inboundCallServiceClient.joinCallRoom(joinRequest)
          );

        if (isKokattoErrorResponse(joinResponse)) {
          const errorMessage = joinResponse.message || joinResponse.reason;
          if (errorMessage?.includes("Session is not ringing")) {
            throw new Error(
              `Failed to join room: ${ERROR_REASON.SESSION_NOT_RINGING}`
            );
          }
          throw new Error(`Failed to join room: ${errorMessage}`);
        }
        setLogLevel(LogLevel.debug);

        // Connect to LiveKit room
        const roomOptions: RoomConnectOptions = {
          rtcConfig: {
            iceServers: [
              {
                urls: [
                  "turn:98.85.84.164:3478?transport=udp",
                  "turn:98.85.84.164:3478?transport=tcp",
                ],
                username: "kokatto-turn-user",
                credential: "k0katt0s3cr3t",
              },
            ],
            iceCandidatePoolSize: 10, // Optional
            iceTransportPolicy: "relay",
          },
        };
        const wsUrl = process.env.VUE_APP_LIVEKIT_WS as string;
        await this.livekitRoom.connect(wsUrl, tokenResponse.token, roomOptions);

        console.log("Connected to LiveKit room:", this.livekitRoom);

        await this.initializeAudioStream();
        return this.room;
      } else {
        throw new Error("LiveKit room instance not initialized");
      }
    } catch (error) {
      throw new Error(`Room connection failed: ${error}`);
    }
  }

  /**
   * Disconnects from the LiveKit room and updates server state
   * @returns {Promise<CallRoomModel | { err: string }>} Success or error result
   */
  async disconnectFromRoom(): Promise<CallRoomModel | { err: string }> {
    return new Promise((resolve, reject) => {
      if (!this.livekitRoom) {
        reject({ err: "No active room connection found" });
        return;
      }

      this.livekitRoom
        .disconnect(true)
        .then(async () => {
          const leaveRequest = {
            roomId: this.room.roomId,
            sessionId: this.session.id,
            remoteId: this.session.remoteId,
            csId: store.getters.getCustomerService.id ?? "",
            csName: store.getters.getCustomerService.csName ?? "",
          };

          const leaveResponse =
            await RefreshableRequestHelper.requestKokatto<LeaveCallRoomResponse>(
              () => this.inboundCallServiceClient.leaveCallRoom(leaveRequest)
            );

          if (isKokattoErrorResponse(leaveResponse)) {
            throw new Error(
              `Failed to update leave room status: ${
                leaveResponse.message || leaveResponse.reason || "Unknown error"
              }`
            );
          }
          resolve(this.room);
        })
        .catch((error) => reject({ err: error.message }));
    });
  }

  /**
   * Toggles the audio mute state
   */
  toggleAudioMute(): void {
    if (!this.audioElement) {
      console.warn("[WARN] Cannot toggle mute: No audio element available");
      return;
    }

    if (this.audioElement.isMuted) {
      this.livekitRoom?.localParticipant.setMicrophoneEnabled(true);
      this.audioElement.unmute().then(() => (this.onMute = false));
    } else {
      this.audioElement.mute().then(() => (this.onMute = true));
      this.livekitRoom?.localParticipant.setMicrophoneEnabled(false);
    }
  }

  /**
   * Toggles the call hold state
   * @returns {Promise<CallRoomModel | Error>} Updated room model or error
   */
  async toggleHoldState(): Promise<CallRoomModel | Error> {
    const previousState = this.holdStatus;
    this.holdStatus = HoldStatus.LOADING;

    try {
      const holdRequest: HoldCallRoomRequest = {
        sessionId: this.session.id,
        roomId: this.room.roomId,
        phoneNumber: this.room.customerPhoneNumber,
      };

      const holdResponse =
        await RefreshableRequestHelper.requestKokatto<HoldCallRoomResponse>(
          () => this.inboundCallServiceClient.holdCallPhone(holdRequest)
        );

      if (isKokattoErrorResponse(holdResponse)) {
        throw new Error(
          `Hold state change failed: ${
            holdResponse.message || holdResponse.reason
          }`
        );
      }

      this.holdStatus =
        previousState === HoldStatus.ON ? HoldStatus.OFF : HoldStatus.ON;
      return this.room;
    } catch (error) {
      this.holdStatus = previousState;
      // this.toggleAudioMute();
      throw new Error(`Hold operation failed: ${error}`);
    }
  }

  /**
   * Initializes and publishes the local audio stream
   * @private
   */
  private async initializeAudioStream(): Promise<void> {
    if (!this.livekitRoom) {
      console.warn("[WARN] Cannot initialize audio: No active room");
      return;
    }

    try {
      this.livekitRoom.remoteParticipants.forEach(
        (participant: RemoteParticipant) => {
          participant.trackPublications.forEach(
            (publication: RemoteTrackPublication) => {
              console.log("PUBLICATION Track info:", {
                name: publication.trackName,
                sid: publication.trackSid,
                kind: publication.track?.kind,
                mimeType: publication.mimeType,
                readyState: (publication.track as any)?.mediaStreamTrack
                  ?.readyState,
                enabled: (publication.track as any)?.mediaStreamTrack?.enabled,
              });
              if (publication.isSubscribed && publication.track) {
                const track = publication.track;
                if (track.kind === "audio") {
                  const audioEl = track.attach();
                  audioEl.autoplay = true;
                  audioEl.muted = false;
                  audioEl
                    .play()
                    .then(() => {
                      console.log("Playback started");
                    })
                    .catch((e: any) => {
                      console.error("Playback failed:", e);
                    });
                  document.body.appendChild(audioEl);
                  console.log("Attached audio:", audioEl);
                }
              }
            }
          );
        }
      );
      this.livekitRoom?.on(
        "trackSubscribed",
        (track: RemoteTrack, publication, participant) => {
          if (track.kind === "audio") {
            // play SIP user's audio
            const audioEl = track.attach();
            audioEl.autoplay = true;
            audioEl.muted = false;
            audioEl.play();
            this.livekitRoom?.once("disconnected", () => {
              track.detach(audioEl);
              audioEl.remove();
            });
          }
        }
      );
      this.livekitRoom.on(
        "participantPermissionsChanged",
        async (previous: any, current: any) => {
          if (current.permissions.canPublish == true && this.livekitRoom) {
            try {
              this.audioElement = await createLocalAudioTrack();
              await this.livekitRoom.localParticipant.publishTrack(
                this.audioElement
              );
              this.onMute = false;
            } catch (error) {
              console.error("Failed to republish audio track:", error);
            }
          }
        }
      );

      this.audioElement = await createLocalAudioTrack();

      const publishResult =
        await this.livekitRoom.localParticipant.publishTrack(this.audioElement);
    } catch (error) {
      console.error("[ERROR] Failed to publish audio track:", error);
    }
  }

  /**
   * Toggles the UI expansion state
   */
  toggleUIExpansion(): void {
    this.isExpanded = !this.isExpanded;
  }

  async _checkMicPermission() {
    const micStatus = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return micStatus.state;
  }
}

export class CallRoomWhatsappController implements CallRoomController {
  room: CallRoomModel;
  session: CallSessionModel;
  onMute: boolean;
  holdStatus: HoldStatus;
  isTransferring: boolean;
  isExpanded: boolean;
  incomingCallConnection: CallConnectionModel | null;
  audioinputDevices: MediaDeviceInfo[];
  speakerDevices: MediaDeviceInfo[];
  startCallDate: number;
  inboundCallServiceClient: InboundCallServiceClient;
  constructor(_room: CallRoomModel, _session: CallSessionModel) {
    this.room = _room;
    this.session = _session;
    this.onMute = false;
    this.holdStatus = HoldStatus.OFF;
    this.isTransferring = false;
    this.isExpanded = true;
    this.incomingCallConnection = null;
    this.audioinputDevices = [];
    this.speakerDevices = [];
    this.startCallDate = 0;
    this.inboundCallServiceClient = new InboundCallServiceClient({
      token: store.getters.getKokattoTokenAccess,
    });
  }

  async connectToRoom() {
    try {
      if (!this.room || !this.session) {
        throw new Error("Room or session is undefined");
      }
      const micStatus = await this._checkMicPermission();
      if (micStatus !== "granted") {
        if (micStatus === "denied") {
          throw new Error(ERROR_REASON.MIC_DENIED);
        } else {
          throw new Error(ERROR_REASON.MIC_NOT_GRANTED);
        }
      }
      this.incomingCallConnection = new CallConnectionModel(this.session.id);
      this._detectDevice();
      this.incomingCallConnection.audioInputDevices = this.audioinputDevices;
      this.incomingCallConnection.audioOutputDevices = this.speakerDevices;
      await this._createPeerConnection();
      await this._preAcceptIncomingCall();
      return this.room;
    } catch (error) {
      console.error("Error joining call", error);
      return new Error("Error joining call " + error);
    }
  }

  _detectDevice() {
    navigator.mediaDevices
      .enumerateDevices()
      .then((deviceInfos) => {
        let hasAudioDevice = false;
        let hasSpeakerDevice = false;

        for (let i = 0; i < deviceInfos.length; i++) {
          if (deviceInfos[i].kind === "audioinput") {
            hasAudioDevice = true;
            this.audioinputDevices.push(deviceInfos[i]);
          } else if (deviceInfos[i].kind === "audiooutput") {
            hasSpeakerDevice = true;
            this.speakerDevices.push(deviceInfos[i]);
          }
        }
      })
      .catch((e) => {
        console.error("Error enumerating devices", e);
        throw new Error("Error enumerating devices");
      });
  }

  async _createPeerConnection() {
    try {
      if (!this.incomingCallConnection) {
        throw new Error("Incoming Call Error");
      }

      // setup config for RTC Connection
      const config = {
        iceServers: [
          {
            urls: [
              "turn:98.85.84.164:3478?transport=udp",
              "turn:98.85.84.164:3478?transport=tcp",
            ],
            username: "kokatto-turn-user",
            credential: "k0katt0s3cr3t",
          },
        ],
        iceCandidatePoolSize: 10,
      };

      // initiate RTC peer connection
      const peerConnection = new RTCPeerConnection(config);

      // add local stream on call connection model
      this.incomingCallConnection.localStream =
        await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });

      // initiate remote stream
      const remoteStream = new MediaStream();

      // add localStream to peer connection
      for (const track of this.incomingCallConnection.localStream.getTracks()) {
        peerConnection.addTrack(track, this.incomingCallConnection.localStream);
      }

      // event ontrack when remote peer add track to their device
      // so we can add remote stream to our system through <audio>
      peerConnection.ontrack = (event) => {
        if (!this.incomingCallConnection) {
          return;
        }

        event.streams[0].getTracks().forEach((track) => {
          this.incomingCallConnection?.remoteStream.addTrack(track);
        });

        // const audioElement: any = this.$refs.remoteAudio;
        const audioElement = document.createElement("audio");
        audioElement.srcObject = remoteStream;
        audioElement.autoplay = true;
        audioElement.play();

        console.log(
          "InboundCall - localStream",
          this.incomingCallConnection.localStream.getAudioTracks()
        );

        console.log(
          "InboundCall - remoteStream",
          this.incomingCallConnection.remoteStream.getAudioTracks()
        );

        // initiate mixAudio Track for combining remote and local stream for recording
        const mixedAudioStream = this._mixAudioTracks(
          this.incomingCallConnection.localStream,
          this.incomingCallConnection.remoteStream
        );

        const combinedStream = new MediaStream();

        mixedAudioStream
          .getAudioTracks()
          .forEach((track) => combinedStream.addTrack(track));
      };

      this.incomingCallConnection.remoteStream = remoteStream;
      this.incomingCallConnection.peerConnection = peerConnection;
    } catch (error) {
      console.error("Error creating peer connection", error);
      throw new Error("Error creating peer connection");
    }
  }

  _mixAudioTracks(localStream: MediaStream, remoteStream: MediaStream) {
    const audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();

    // Connect local audio track to the audio context
    if (localStream && localStream.getAudioTracks().length > 0) {
      const localSource = audioContext.createMediaStreamSource(
        new MediaStream([localStream.getAudioTracks()[0]])
      );
      localSource.connect(destination);
    }

    // Connect remote audio track to the audio context
    if (remoteStream && remoteStream.getAudioTracks().length > 0) {
      const remoteSource = audioContext.createMediaStreamSource(
        new MediaStream([remoteStream.getAudioTracks()[0]])
      );
      remoteSource.connect(destination);
    }

    // Return the mixed audio stream
    return destination.stream;
  }

  async _preAcceptIncomingCall() {
    try {
      if (!this.incomingCallConnection) {
        throw new Error("Incoming Call Error");
      }

      console.log(
        "InboundCall - preAcceptIncomingCall",
        JSON.parse(this.session.sdp || "{")
      );

      const session: {
        sdp: string;
        type: string;
      } = JSON.parse(this.session.sdp || "{}");

      await this.incomingCallConnection.peerConnection.setRemoteDescription(
        new RTCSessionDescription({
          sdp: session.sdp,
          type: "offer",
        })
      );

      //create answer sdp
      const answerDescription =
        await this.incomingCallConnection?.peerConnection.createAnswer();

      // add local sdp to our peer connection
      await this.incomingCallConnection?.peerConnection.setLocalDescription(
        answerDescription
      );

      // event peer connection
      this.incomingCallConnection.peerConnection.onconnectionstatechange =
        () => {
          const state =
            this.incomingCallConnection?.peerConnection.connectionState;
          console.log("InboundCall - Connection State Alda: ", state);

          if (state === "disconnected") {
            console.log(
              "InboundCall - The peer connection has been disconnected."
            );
            // Handle disconnection logic here
          } else if (state === "failed") {
            console.log("InboundCall - The peer connection has failed.");
            // Handle failure logic here
          } else if (state === "closed") {
            console.log("InboundCall - The connection has been closed.");
            // Handle closure logic here
          }
        };

      // event peer connection
      this.incomingCallConnection.peerConnection.onsignalingstatechange =
        () => {
          const state =
            this.incomingCallConnection?.peerConnection.signalingState;
          console.log("InboundCall - Connection State Alda state: ", state);
        };

      // event peer connection
      this.incomingCallConnection.peerConnection.oniceconnectionstatechange =
        () => {
          const iceState =
            this.incomingCallConnection?.peerConnection.iceConnectionState;
          console.log("InboundCall - Connection State Alda ice: ", iceState);
        };

      // event peer connection
      this.incomingCallConnection.peerConnection.ondatachannel = (event) => {
        const receivedChannel = event.channel;

        // Set up received data channel event listeners
        receivedChannel.onopen = () => {
          console.log("InboundCall - Peer B: Data channel is open");
        };
        receivedChannel.onmessage = (messageEvent) => {
          console.log(
            "InboundCall - Peer B received message:",
            messageEvent.data
          );
        };
        receivedChannel.onclose = () => {
          console.log("InboundCall - Peer B: Data channel is closed");
        };
      };

      await new Promise<void>((resolve) => {
        if (!this.incomingCallConnection?.peerConnection) {
          resolve();
        }

        if (
          this.incomingCallConnection?.peerConnection?.iceGatheringState ===
          "complete"
        ) {
          resolve();
        } else {
          const peerConnection = this.incomingCallConnection?.peerConnection;
          if (peerConnection) {
            peerConnection.onicegatheringstatechange = () => {
              if (peerConnection.iceGatheringState === "complete") {
                peerConnection.removeEventListener(
                  "icegatheringstatechange",
                  () => {
                    console.log("InboundCall - ICE Gathering State Changed");
                  }
                );
                resolve();
              }
            };
          }
        }

        setTimeout(() => {
          resolve();
        }, 3000);
      });

      const answer: InboundCallAcceptRequest = {
        csId: store.getters.getCustomerService.id ?? "",
        remoteId: this.session.remoteId,
        roomId: this.session.roomId,
        sessionId: this.session.id,
        csName: store.getters.getCustomerService.csName ?? "",
        answer: {
          type: "answer",
          sdp:
            this.incomingCallConnection?.peerConnection?.localDescription
              ?.sdp ?? "",
        },
      };
      console.log("offer", session.sdp);
      console.log(
        "answer",
        this.incomingCallConnection?.peerConnection?.localDescription?.sdp
      );
      try {
        const acceptResponse =
          await RefreshableRequestHelper.requestKokatto<ProcessingIncomingCallResponse>(
            () => {
              return this.inboundCallServiceClient.inboundCallAccept(answer);
            }
          );
        console.log("InboundCall, acceptResponse", acceptResponse);
        if (isKokattoErrorResponse(acceptResponse)) {
          throw new Error(
            `Failed to accept call: ${
              acceptResponse.message || acceptResponse.reason
            }`
          );
        }
      } catch (error: any) {
        console.error("Error Accept Call", error);
        throw new Error("Error Accept Call");
      }
    } catch (error) {
      console.error("Error preAcceptIncomingCall", error);
      throw new Error("Error preAcceptIncomingCall");
    }
  }

  toggleAudioMute() {
    this.onMute = !this.onMute;
    const audioInputDevices =
      this.incomingCallConnection?.localStream.getAudioTracks() || [];
    if (audioInputDevices.length) {
      audioInputDevices[0].enabled = !this.onMute;
    }
    return;
  }
  async disconnectFromRoom(): Promise<CallRoomModel | { err: string }> {
    try {
      const payload: InboundCallRejectIncomingRequest = {
        csId: store.getters.getCustomerService.id ?? "",
        remoteId: this.session.remoteId,
        roomId: this.session.roomId,
        sessionId: this.session.id,
        csName: store.getters.getCustomerService.csName ?? "",
      };

      const terminateResponse =
        await RefreshableRequestHelper.requestKokatto<ProcessingIncomingCallResponse>(
          () => {
            return this.inboundCallServiceClient.terminateCall(payload);
          }
        );
      if (isKokattoErrorResponse(terminateResponse)) {
        throw new Error("Error terminating call");
      }
      if (this.session.status === CallSessionStatus.ANSWERED) {
        this.incomingCallConnection?.cleanup();
      }
      return this.room;
    } catch (error) {
      console.error("Error disconnecting call", error);
      throw new Error("Error disconnecting call " + error);
    }
  }
  toggleUIExpansion() {
    this.isExpanded = !this.isExpanded;
  }
  async toggleHoldState(): Promise<CallRoomModel | Error> {
    const previousState = this.holdStatus;
    this.holdStatus = HoldStatus.LOADING;
    try {
      const payload: HoldCallRoomRequest = {
        roomId: this.room.roomId,
        sessionId: this.session.id,
        phoneNumber: this.room.customerPhoneNumber,
      };
      const holdResponse = await this.inboundCallServiceClient.holdCallWhatsapp(
        payload
      );
      this.holdStatus =
        previousState === HoldStatus.ON ? HoldStatus.OFF : HoldStatus.ON;
      if (isKokattoErrorResponse(holdResponse)) {
        throw new Error(`Error holding call: ${holdResponse.message}`);
      }
      return this.room;
    } catch (error) {
      this.holdStatus = previousState;
      console.error("[ERROR] Failed to initiate call transfer:", error);
      throw new Error(`Error holding call: ${error}`);
    }
  }
  async _checkMicPermission() {
    const micStatus = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return micStatus.state;
  }
}
